import { useMemo } from 'react';

import { useApi } from './use-api';
import { useApprovalQueue, type ApprovalQueueKind } from './use-approval-queue';
import { useAssociations, useFollowUps, useMessages, useTasks } from './use-data';
import { useFeatureFlags } from './use-feature-flags';
import { usePermissions } from './use-permissions';

import type { InboxMessage } from '@/services/data-service';
import type { FollowUp, Task, WorkOrder } from '@/types/models';
import type { ApprovalSlaStatus } from '@/utils/approval-sla';
import { customerFacingDescription, isReturnedForReview } from '@/utils/approval-state';
import { DAY_MS, dueDayMs, etDayMs } from '@/utils/date-utils';
import { NEEDS_HUMAN_FILTER } from '@/utils/inbox-triage';

/**
 * Everything the signed-in user has to act on, merged and sorted (#279).
 *
 * Three sources — the approval/review queue, follow-ups a technician flagged, and ordinary
 * tasks — in three tables and three features, but one question: what do I have to do.
 *
 * ── EXTRACTED SO THE PANEL AND THE PAGE CANNOT DISAGREE ──
 *
 * The dashboard panel shows the first few and the full page shows all of them. That is the
 * exact shape that produced the 280 / 86 / 0 bug one level up, where four surfaces each
 * built their own version of "the queue". The merge, the labels and the sort live here; a
 * consumer only chooses how many rows to render.
 */

/**
 * `returned` is a review row a property manager sent back (#503): still accounting's to
 * redo, but pinned to the top and carrying the PM's reason in place of the description.
 */
/**
 * `inbox-triage` and `inbox-assigned` are mail, not work orders (#532 follow-up).
 *
 * The To Do card claimed to be "everything waiting on this user" while ignoring the inbox
 * entirely — which for support, whose whole job is the triage queue, meant it was empty
 * while they had fifty things to do.
 *
 * Two kinds rather than one because they are answered by different people: triage is
 * "nobody owns this yet", assigned is "this one is yours". Both are naturally scoped
 * server-side, so a property manager sees their own associations' slice and support sees
 * the portfolio — no role check here, which is what keeps the two surfaces honest.
 */
export type TodoKind =
	| 'approval' | 'review' | 'returned' | 'follow-up' | 'task'
	| 'inbox-triage' | 'inbox-assigned';

export interface TodoItem {
	id: string;
	kind: TodoKind;
	title: string;
	/** Primary context — the job location for a work order. Rendered at normal weight. */
	subtitle?: string;
	/**
	 * First line of the description, shown dimmed after the subtitle like an email
	 * preheader. Separate from `subtitle` so the row can style the two differently; folding
	 * them into one string would make the location and the prose indistinguishable.
	 */
	preview?: string;
	/** ET business-date marker, or null when the item has no deadline. */
	dueMs: number | null;
	overdue: boolean;
	/** Sort weight within an equal due date; lower is more urgent. */
	rank: number;
	dueLabel: string;
	workOrderId?: string;
	taskId?: string;
	sla?: ApprovalSlaStatus | null;
	/** Set on the two inbox kinds, so a row can open `/inbox/<id>`. */
	messageId?: string;
	/**
	 * Derived cost in cents, on work-order rows only.
	 *
	 * Undefined when the API did not supply one, NOT zero — a real $0.00 work order and a
	 * missing figure must not look the same on a row somebody is about to approve.
	 */
	costCents?: number;
	/** The association the work order or task belongs to — shown on the row and in confirmations. */
	associationName?: string;
	/** Full work-requested text, for the Approve confirmation. Work-order rows only. */
	workRequested?: string;
	/** Full customer-facing description (with the completion-note fallback). Work-order rows only. */
	customerDescription?: string;
}

export interface TodoList {
	items: TodoItem[];
	overdueCount: number;
	/** Which approval queue fed this — 'needs_review' rows are not approvable in place. */
	queueKind: ApprovalQueueKind;
	/**
	 * The queue behind these items was capped, so `items` is a page.
	 *
	 * Passed through rather than swallowed: a panel showing `items.length` as a total is
	 * quietly wrong whenever this is set, and the fix is to SAY the list is partial, not to
	 * print a smaller number as though it were the whole thing.
	 */
	truncated: boolean;
	isLoading: boolean;
	refetch: () => Promise<unknown>;
}

/** Beyond this a task is not something to do now. */
const HORIZON_DAYS = 14;

/**
 * The first line of a description, for the preheader.
 *
 * These are markdown fields, so a description can be several lines with the useful sentence
 * first. Split on the newline rather than truncating at a character count: a hard cut lands
 * mid-word, whereas the first line is a unit the author chose. Trailing markdown bullets and
 * heading marks are stripped so the preview does not open with `- ` or `## `.
 */
function firstLine(text: string | null | undefined): string | undefined {
	const line = String(text ?? '')
		.split(/\r?\n/)
		.map((l) => l.replace(/^\s*(?:[-*+]\s+|#{1,6}\s+|>\s*)/, '').trim())
		.find((l) => l.length > 0);
	return line || undefined;
}

/** ET-anchored, whole-day relative label. Never `new Date()` — see utils/date-utils. */
export function relativeDue(dueMs: number, todayMs: number): { text: string; overdue: boolean } {
	const days = Math.round(((dueDayMs(dueMs) ?? todayMs) - todayMs) / DAY_MS);
	if (days < 0) {
		return { text: days === -1 ? '1 day overdue' : `${Math.abs(days)} days overdue`, overdue: true };
	}
	if (days === 0) return { text: 'Due today', overdue: false };
	if (days === 1) return { text: 'Due tomorrow', overdue: false };
	return { text: `Due in ${days} days`, overdue: false };
}

/** Whole-day "how long ago", ET-anchored like `relativeDue`. */
export function relativeAgo(atMs: number, todayMs: number): string {
	const days = Math.round((todayMs - etDayMs(atMs)) / DAY_MS);
	if (days <= 0) return 'today';
	if (days === 1) return 'yesterday';
	return `${days} days ago`;
}

export function useTodoItems(atMs?: number): TodoList {
	const { user } = useApi();
	const { hasFlag } = useFeatureFlags();

	const queue = useApprovalQueue(atMs);
	const { data: followUpData } = useFollowUps();
	/**
	 * Association names for the work-order and task rows. The work-orders list carries only
	 * `associationId`; the same query the sidebar and dashboard already run resolves it, so
	 * this is served from cache rather than a new request.
	 */
	const { data: associationsData } = useAssociations({ status: 'active', limit: 1000 });
	const { data: taskData } = useTasks({
		assignedTo: user?.id,
		status: 'open,in_progress',
		limit: 200,
		enabled: hasFlag('tasks_enabled') && !!user?.id,
	});

	/**
	 * The two inbox queues, memoised because a fresh object per render is a fresh TanStack
	 * key and therefore a refetch loop.
	 *
	 * Scoping is entirely server-side: `?assignedTo=me` resolves against the caller, and the
	 * triage list is narrowed by `inboxScopeForUser`. Support, being unscoped, sees the whole
	 * triage pile; a property manager sees their own associations'. Neither needs a branch
	 * here, which is what stops this hook and the rail disagreeing.
	 *
	 * UNFILED both times, for the same reason every inbox badge is: a To Do row is a claim
	 * that something still needs attention, and mail that has already produced a work order
	 * or an improvement request does not. Without it this list — and the count on the
	 * dashboard's To Do panel — offered rows the user cannot clear, because the thing they
	 * were meant to do has already been done.
	 */
	// The PLATFORM-aware answer, not the bare flag: native has no inbox to open these rows in.
	const { canUseInbox } = usePermissions();
	const triageQuery = useMemo(() => ({ label: NEEDS_HUMAN_FILTER, limit: 50, unfiled: true }), []);
	const assignedQuery = useMemo(() => ({ assignedTo: 'me', limit: 50, unfiled: true }), []);
	const { data: triageData } = useMessages(triageQuery, { enabled: canUseInbox });
	const { data: assignedData } = useMessages(assignedQuery, { enabled: canUseInbox });

	const nowMs = atMs ?? Date.now();
	const todayMs = etDayMs(nowMs);

	const items = useMemo<TodoItem[]>(() => {
		const out: TodoItem[] = [];
		const associationName = new Map<string, string>(
			((associationsData as { data?: Array<{ id: string; name: string }> } | undefined)?.data ?? [])
				.map((a) => [a.id, a.name]),
		);
		const nameOf = (id: string | null | undefined): string | undefined =>
			(id ? associationName.get(id) : undefined);
		/**
		 * A reviewer's rows are not approvable BY THEM: their step is writing the
		 * customer-facing description, so the row says Review and links to the work order
		 * rather than offering an Approve button that would 403.
		 */
		const isReviewQueue = queue.kind === 'needs_review';

		for (const { workOrder: wo, sla } of queue.rows as Array<{
			workOrder: WorkOrder; sla: ApprovalSlaStatus | null;
		}>) {
			/**
			 * A row the PM SENT BACK (#503). Still a review row — same verb, same action — but
			 * it is the most urgent thing on accounting's list: somebody is waiting, and the
			 * reason they gave is the brief. It gets its own kind so the row can go red, and
			 * the reason takes the preview slot, since the description it replaces is the one
			 * the PM just rejected.
			 */
			const returned = isReviewQueue && isReturnedForReview(wo);
			const kind: TodoKind = returned ? 'returned' : (isReviewQueue ? 'review' : 'approval');
			const label = returned
				? `Sent back ${relativeAgo(Number(wo.reviewReturnedAt), todayMs)}`
				: sla
					? (sla.overdue
						? `${sla.daysOverdue} business day${sla.daysOverdue === 1 ? '' : 's'} overdue`
						: relativeDue(sla.dueMs, todayMs).text)
					: (isReviewQueue ? 'Completed, awaiting review' : 'Awaiting approval');
			const verb = isReviewQueue ? 'Review' : 'Approve';
			out.push({
				id: `${kind}:${wo.id}`,
				kind,
				title: wo.orderNumber ? `${verb} WO #${wo.orderNumber}` : `${verb} work order`,
				subtitle: wo.jobLocation || undefined,
				// The CUSTOMER-facing description, never the internal note — and for a PM the
				// API does not send the internal one at all.
				preview: returned
					? (firstLine(wo.reviewReturnNote) ?? 'No reason given')
					: firstLine(wo.customerDescription),
				dueMs: sla?.dueMs ?? null,
				// A bounce is not an SLA breach — the red on these rows means "somebody is
				// waiting", and counting it as overdue would inflate the header's number.
				overdue: !!sla?.overdue,
				// This queue outranks equally-due work: somebody else is blocked on it.
				rank: 0,
				dueLabel: label,
				workOrderId: wo.id,
				sla,
				costCents: typeof (wo as { estimatedTotalCents?: number }).estimatedTotalCents === 'number'
					? (wo as { estimatedTotalCents?: number }).estimatedTotalCents
					: undefined,
				associationName: nameOf(wo.associationId),
				workRequested: wo.workRequested?.trim() || undefined,
				customerDescription: customerFacingDescription(wo) || undefined,
			});
		}

		for (const f of ((followUpData ?? []) as FollowUp[])) {
			out.push({
				id: `follow-up:${f.taskId}`,
				kind: 'follow-up',
				title: f.orderNumber ? `Follow up on WO #${f.orderNumber}` : 'Follow up on work order',
				subtitle: f.jobLocation || undefined,
				preview: firstLine(f.followUpNote),
				// Follow-ups are flagged, not scheduled — they carry no deadline of their own.
				dueMs: null,
				overdue: false,
				rank: 1,
				dueLabel: 'Flagged for follow-up',
				workOrderId: f.workOrderId,
				taskId: f.taskId,
				associationName: f.associationName ?? nameOf(f.associationId),
			});
		}

		const tasks = ((taskData as { data?: Task[] } | undefined)?.data ?? []) as Task[];
		for (const t of tasks) {
			const due = dueDayMs(t.dueDate);
			if (due === null) continue;
			if (due > todayMs + HORIZON_DAYS * DAY_MS) continue;
			const rel = relativeDue(due, todayMs);
			out.push({
				id: `task:${t.id}`,
				kind: 'task',
				title: t.title,
				// No location on a task, so the description carries the preview slot alone.
				preview: firstLine(t.description),
				dueMs: due,
				overdue: rel.overdue,
				rank: 2,
				dueLabel: rel.text,
				taskId: t.id,
				associationName: nameOf(t.associationId),
			});
		}

		/**
		 * Mail nobody owns yet — the top of the list, per the ask.
		 *
		 * Undated by nature: a message has an arrival time, not a deadline, so `dueMs` is
		 * null and `rank` alone orders it. `-1` puts triage above every work-order row of
		 * the same datedness, because an unowned message is the one thing on this list that
		 * nobody else is going to pick up.
		 */
		for (const m of ((triageData?.data ?? []) as InboxMessage[])) {
			out.push({
				id: `inbox-triage:${m.id}`,
				kind: 'inbox-triage',
				title: m.subject?.trim() || '(no subject)',
				subtitle: m.submitterName || m.fromName || m.fromAddress || undefined,
				preview: firstLine(m.bodyText),
				dueMs: null,
				overdue: false,
				rank: -1,
				dueLabel: 'Needs triage',
				messageId: m.id,
				associationName: m.associationName,
			});
		}

		/**
		 * Mail handed to this person. Ranked just after triage and before the work-order
		 * queues: somebody chose to give it to them, which outranks anything they merely
		 * happen to be in the rotation for.
		 *
		 * A message that is BOTH — assigned and still untriaged — appears once, as triage,
		 * because that is the more urgent description of the same row. Without this the
		 * catch-all rule (which assigns everything to a PM) would double every triage item.
		 */
		const triageIds = new Set(((triageData?.data ?? []) as InboxMessage[]).map((m) => m.id));
		for (const m of ((assignedData?.data ?? []) as InboxMessage[])) {
			if (triageIds.has(m.id)) continue;
			out.push({
				id: `inbox-assigned:${m.id}`,
				kind: 'inbox-assigned',
				title: m.subject?.trim() || '(no subject)',
				subtitle: m.submitterName || m.fromName || m.fromAddress || undefined,
				preview: firstLine(m.bodyText),
				dueMs: null,
				overdue: false,
				rank: 0,
				dueLabel: 'Assigned to you',
				messageId: m.id,
				associationName: m.associationName,
			});
		}

		/**
		 * Sent-back rows first, then overdue, then soonest, then by kind.
		 *
		 * The bounce outranks even overdue work: an overdue item is late for a deadline, a
		 * bounced one has a person actively waiting on it. Undated items (follow-ups) sort
		 * AFTER dated ones rather than to the top, which is where a `null`-treated-as-0
		 * comparison would put them.
		 */
		return out.sort((a, b) => {
			const aBack = a.kind === 'returned';
			const bBack = b.kind === 'returned';
			if (aBack !== bBack) return aBack ? -1 : 1;
			if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
			if (a.dueMs != null && b.dueMs != null && a.dueMs !== b.dueMs) return a.dueMs - b.dueMs;
			if ((a.dueMs == null) !== (b.dueMs == null)) return a.dueMs == null ? 1 : -1;
			return a.rank - b.rank;
		});
	}, [queue.rows, queue.kind, followUpData, taskData, associationsData, triageData, assignedData, todayMs]);

	return {
		items,
		overdueCount: items.filter((i) => i.overdue).length,
		queueKind: queue.kind,
		truncated: queue.truncated,
		isLoading: queue.isLoading,
		refetch: queue.refetch,
	};
}
